/**
 * Keeps the query cache live from /ws: trades are prepended to their coin's trade list and the ticker; coin updates
 * patch every cached list and detail in place; status changes patch status and the CPMM pool. Curve fields of an
 * open coin are refreshed with a debounced refetch (the coin message carries only summary fields). After a
 * reconnect, whatever is on screen is refetched: the server does not replay missed messages.
 */
import { useEffect } from 'react';
import { type InfiniteData, type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useTicker } from '@/store/ticker';
import { live } from './live';
import { qk } from './queries';
import type { CoinDetail, CoinsResponse, CoinSummary, Trade, TradesResponse, WsServerMessage } from './types';

const tradeKey = (t: Trade) => `${t.sig}:${t.ix}`;

function patchLists(qc: QueryClient, mint: string, patch: (c: CoinSummary) => CoinSummary) {
  qc.setQueriesData<CoinsResponse>({ queryKey: ['coins'] }, (old) => {
    if (!old?.items?.some((c) => c.mint === mint)) return old;
    return { ...old, items: old.items.map((c) => (c.mint === mint ? patch(c) : c)) };
  });
  qc.setQueriesData<InfiniteData<CoinsResponse>>({ queryKey: ['coins-inf'] }, (old) => {
    if (!old?.pages?.some((p) => p.items.some((c) => c.mint === mint))) return old;
    return { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.map((c) => (c.mint === mint ? patch(c) : c)) })) };
  });
}

function insertNew(qc: QueryClient, coin: CoinSummary) {
  qc.setQueriesData<CoinsResponse>({ queryKey: ['coins', 'new'] }, (old) => {
    if (!old || old.items.some((c) => c.mint === coin.mint)) return old;
    const limit = Math.max(old.items.length, 1);
    return { ...old, items: [coin, ...old.items].slice(0, limit) };
  });
}

export function useLiveSync() {
  const qc = useQueryClient();
  useEffect(() => {
    live.start();
    const unsubAll = live.subscribeAll();
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const debounce = (key: string, ms: number, fn: () => void) => {
      if (timers.has(key)) return;
      timers.set(
        key,
        setTimeout(() => {
          timers.delete(key);
          fn();
        }, ms),
      );
    };
    const off = live.on((m: WsServerMessage) => {
      if (m.type === 'trade') {
        const t = m.trade;
        useTicker.getState().pushTrade(t);
        qc.setQueryData<TradesResponse>(qk.trades(t.mint), (old) => {
          if (!old) return old;
          if (old.items.some((x) => tradeKey(x) === tradeKey(t))) return old;
          return { items: [t, ...old.items].slice(0, 300) };
        });
        if (qc.getQueryState(qk.coin(t.mint))) debounce(`coin:${t.mint}`, 700, () => void qc.invalidateQueries({ queryKey: qk.coin(t.mint), refetchType: 'active' }));
        if (qc.getQueryState(qk.holders(t.mint))) debounce(`holders:${t.mint}`, 4000, () => void qc.invalidateQueries({ queryKey: qk.holders(t.mint), refetchType: 'active' }));
      } else if (m.type === 'coin') {
        const c = m.coin;
        useTicker.getState().learn([c]);
        patchLists(qc, c.mint, (old) => ({ ...old, ...c }));
        insertNew(qc, c);
        qc.setQueryData<CoinDetail>(qk.coin(c.mint), (old) => (old ? { ...old, ...c } : old));
      } else if (m.type === 'status') {
        const patch = (c: CoinSummary) => ({ ...c, status: m.status, ...(m.cpmmPool ? { cpmmPool: m.cpmmPool } : {}) });
        patchLists(qc, m.mint, patch);
        qc.setQueryData<CoinDetail>(qk.coin(m.mint), (old) => (old ? (patch(old) as CoinDetail) : old));
        void qc.invalidateQueries({ queryKey: qk.coin(m.mint), refetchType: 'active' });
      }
    });
    const offReconnect = live.onReconnect(() => {
      for (const key of ['coin', 'trades', 'candles', 'holders', 'coins', 'coins-inf']) void qc.invalidateQueries({ queryKey: [key], refetchType: 'active' });
    });
    return () => {
      off();
      offReconnect();
      unsubAll();
      for (const t of timers.values()) clearTimeout(t);
    };
  }, [qc]);
}
